import { SxProps, Theme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';
import React from 'react';

import { useImmer } from 'use-immer';

import { Company, Contact, Customer, CustomerSearchDTO, Location } from '_lib/api/apiTypes.shim';
import { Box } from '_lib/components/Box/Box';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';

import { CustomerContext, PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { toSxArray } from '_lib/utils/helpers';

import { CustomerDetail } from './CustomerDetail/CustomerDetail';
import { CustomerSelection } from './CustomerSelection/CustomerSelection';

export type CustomerPaneProps = {
  phoneCallURLState: PhoneCallURLState;
  hideAllTabs?: boolean; // This is required because nested tabs are not 'rendered', causing an error
  company: Company;
  customerContext: CustomerContext | null;
  location: Location | undefined;
  setCustomerContext: (customerContext: CustomerContext | null) => void;
  setCustomerContextLocations: (locations: Location[]) => void;
  sx?: SxProps<Theme>;
};

export const CustomerPane = (props: CustomerPaneProps): React.ReactElement => {
  const {
    phoneCallURLState,
    sx,
    hideAllTabs,
    company,
    customerContext,
    location,
    setCustomerContext,
    setCustomerContextLocations
  } = props;

  const [customerSearchResults, setCustomerSearchResults] = useImmer<CustomerSearchDTO[]>([]);

  const [locationContactsMap, setLocationContactsMap] = useImmer<{ [locationProviderId: string]: Contact[] }>({});

  /**
   * checks to see if an updated customer is in the cached customers search list. If it is, we replace the item in the
   * list so that it isn't stale.
   */
  const handleCustomerSearchListUpdate = (updatedCustomer: Customer) => {
    setCustomerSearchResults((searchResults) =>
      searchResults.map((c) =>
        c.providerId === updatedCustomer.providerId ? { ...updatedCustomer, locations: c.locations } : c
      )
    );
  };

  const handleSetCustomerContext = (selectedCustomerContext: CustomerContext | null) => {
    // Clear location contact cache if we switch into a new customer
    if (
      selectedCustomerContext &&
      selectedCustomerContext.customer.providerId !== customerContext?.customer.providerId
    ) {
      setLocationContactsMap({});
    }
    setCustomerContext(selectedCustomerContext);
  };

  const handleSetCustomerLocations = (locations: Location[]) => setCustomerContextLocations(locations);

  const handleLocationContactsMapUpdated = (locationProviderId: string, contacts: Contact[]) => {
    setLocationContactsMap((prevMap) => {
      prevMap[locationProviderId] = contacts;
    });
  };

  return (
    <>
      <Box
        sx={[
          {
            display: 'flex',
            flexDirection: 'column'
          },
          ...toSxArray(sx)
        ]}>
        <ErrorBoundary
          beforeCapture={(scope) => {
            scope.setTag('pane', 'Customer Pane');
          }}
          fallback={({ resetError }) => (
            <ErrorBoundaryFallback
              title='Error Displaying Customers'
              onRefresh={() => {
                resetError();
              }}
            />
          )}>
          {customerContext && (
            <CustomerDetail
              onCustomerUpdated={(customer) => {
                handleSetCustomerContext({
                  ...customerContext,
                  customer: { ...customerContext.customer, ...customer }
                });
                handleCustomerSearchListUpdate(customer);
              }}
              onCustomerLocationsUpdated={handleSetCustomerLocations}
              onLocationContactsUpdated={handleLocationContactsMapUpdated}
              onCloseCustomer={() => {
                handleSetCustomerContext(null);
              }}
              customerContext={customerContext}
              selectedLocation={location}
              locationContactsMap={locationContactsMap}
              phoneCallURLState={phoneCallURLState}
              company={company}
              hideAllTabs={hideAllTabs}
            />
          )}
          <div
            style={
              // NOTE - WE NEED THIS MOUNTED AT ALL TIMES BECAUSE OF CUSTOMER SEARCH ON INIT/CUSTOMER SWITCH
              customerContext
                ? { visibility: 'hidden', width: '0px', height: '0px' }
                : { visibility: 'visible', width: '100%', height: '100%' }
            }>
            <CustomerSelection
              onSetCustomerContext={handleSetCustomerContext}
              onSetCustomerSearchResults={setCustomerSearchResults}
              companyId={company.id}
              callerId={phoneCallURLState?.callerId}
              customers={customerSearchResults}
              hideAllTabs={hideAllTabs}
            />
          </div>
        </ErrorBoundary>
      </Box>
    </>
  );
};
